import * as React from 'react';
import { css } from '@emotion/react';
import { FixMeLater } from '../../utils';
import { useLazyImage, useForkRef, useIsomorphicEffect } from '../../hooks';
import { useHoneycombTheme } from '../../theming';

export type LazyImageProps = {
    /**
     * The `alt` attribute of the image.
     */
    alt?: string;

    /**
     * Any CSS transitions which should be applied to the image. These will be appended to the default opacity transition.
     */
    cssTransitions?: string[];

    /**
     * Set to `true` if the image is the child of a `picture` element.
     */
    picture?: boolean;

    /**
     * Callback fired when the image has loaded.
     */
    onImageLoaded?: () => void;

    /**
     * Callback fired if the image fails to load.
     */
    onImageError?: () => void;

    /**
     * The `src` attribute of the image. Not relevant if the image is the child of a `picture` element.
     */
    src?: string;

    /**
     * The `title` attribute of the image.
     */
    title?: string;

    /**
     * The src of a placeholder image to be displayed if the image fails to load.
     */
    placeholderSrc?: string;

    /**
     * Permit additional properties which will be passed to the root node.
     */
    [x: string]: unknown;
};

/**
 * @deprecated LazyImage is depreacted. Please use `Image` which also has the lazy-load functionality.
 */
export const LazyImage: React.ForwardRefExoticComponent<LazyImageProps & React.RefAttributes<HTMLElement>> =
    React.forwardRef(function LazyImage(props: LazyImageProps, ref: React.ForwardedRef<FixMeLater>): React.JSX.Element {
        const {
            alt,
            cssTransitions = [],
            onImageError,
            onImageLoaded,
            picture,
            src,
            title,
            placeholderSrc,
            ...other
        } = props;

        const [imageLoaded, setImageLoaded] = React.useState(false);

        const [lazyImageRef] = useLazyImage();
        // If there is no LazyImageProvider then lazyImageRef will be null.
        const lazyLoading = lazyImageRef !== null;

        const imageRef = React.useRef<HTMLImageElement | null>(null);
        const handleRefIntermediary = useForkRef(imageRef, lazyImageRef);
        const handleRef = useForkRef(handleRefIntermediary, ref);

        const handleImageLoaded = React.useCallback(() => {
            setImageLoaded(true);
            if (onImageLoaded) {
                onImageLoaded();
            }
        }, [onImageLoaded]);

        const handleImageError = React.useCallback(() => {
            if (placeholderSrc) {
                if (imageRef.current) {
                    // Set the placeholder image
                    imageRef.current.src = placeholderSrc;
                }
            }
            if (onImageError) {
                onImageError();
            }
        }, [onImageError, placeholderSrc]);

        useIsomorphicEffect(() => {
            if (!lazyLoading && imageRef.current && imageRef.current.complete) {
                handleImageLoaded();
            }
        }, []);

        const { theme } = useHoneycombTheme();
        const { transitions } = theme;
        const { duration, easing } = transitions;

        cssTransitions.unshift(`opacity ${duration.standard}ms ${easing.easeInOut}`);

        const styles = css`
            opacity: 0;
            &[data-hidden-on-mount] {
                transition: ${cssTransitions.join(', ')};
            }
            &[data-loaded] {
                opacity: 1;
            }
            vertical-align: middle;
        `;

        return (
            <img
                ref={handleRef}
                css={styles}
                data-src={lazyLoading && !picture ? src : undefined}
                src={!lazyLoading && !picture ? src : undefined}
                data-in-picture={picture || undefined}
                data-loaded={imageLoaded || undefined}
                alt={alt || ''}
                aria-label={title}
                title={title}
                onLoad={handleImageLoaded}
                onError={handleImageError}
                {...other}
            />
        );
    });
